<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { IG_TEMPLATE_TYPES } from 'dashboard/helper/instagramTemplateHelper';

const props = defineProps({
  /**
   * Either a MessageTemplate `content` object from the builder, or the payload
   * actually sent to Instagram (stored on the message as `ig_template`). The
   * sent payload nests the template under attachment.payload, so unwrap it.
   */
  content: { type: Object, default: () => ({}) },
  /** Values for {{1}}, {{2}}, ... keyed by placeholder number. */
  variables: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const payload = computed(() => {
  const attachmentPayload = props.content?.attachment?.payload;
  if (attachmentPayload) return attachmentPayload;
  return props.content || {};
});

// A sent quick-reply payload has no template_type: it is plain text plus chips.
const templateType = computed(
  () =>
    payload.value.template_type ||
    (payload.value.quick_replies ? IG_TEMPLATE_TYPES.QUICK_REPLY : null)
);

const interpolate = text => {
  if (!text) return '';
  return String(text).replace(
    /\{\{(\d+)\}\}/g,
    (match, key) => props.variables[key] ?? match
  );
};

const elements = computed(() => payload.value.elements || []);
const buttons = computed(() => payload.value.buttons || []);
const quickReplies = computed(
  () => props.content?.quick_replies || payload.value.quick_replies || []
);
const text = computed(() =>
  interpolate(props.content?.text || payload.value.text)
);

const isGeneric = computed(
  () => templateType.value === IG_TEMPLATE_TYPES.GENERIC
);
const isButton = computed(
  () => templateType.value === IG_TEMPLATE_TYPES.BUTTON
);
const quickReplyLabel = reply => {
  const label = interpolate(reply.title);
  if (label) return label;

  const contentType = reply.content_type || reply.contentType;
  return contentType
    ? t(
        `MESSAGE_TEMPLATES.INSTAGRAM.CONTENT_TYPES.${contentType.toUpperCase()}`
      )
    : '';
};

const isQuickReply = computed(
  () => templateType.value === IG_TEMPLATE_TYPES.QUICK_REPLY
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Generic: horizontally scrollable carousel of cards -->
    <div v-if="isGeneric" class="flex gap-3 pb-2 overflow-x-auto">
      <div
        v-for="(element, index) in elements"
        :key="index"
        class="flex flex-col overflow-hidden border rounded-xl border-n-weak bg-n-solid-1 w-56 shrink-0"
      >
        <img
          v-if="element.image_url"
          :src="element.image_url"
          :alt="interpolate(element.title)"
          class="object-cover w-full h-32 bg-n-alpha-2"
        />
        <div class="flex flex-col gap-1 p-3">
          <span class="text-sm font-medium break-words text-n-slate-12">
            {{ interpolate(element.title) }}
          </span>
          <span
            v-if="element.subtitle"
            class="text-xs break-words text-n-slate-11"
          >
            {{ interpolate(element.subtitle) }}
          </span>
        </div>
        <div
          v-if="element.buttons?.length"
          class="flex flex-col border-t border-n-weak"
        >
          <span
            v-for="(button, buttonIndex) in element.buttons"
            :key="buttonIndex"
            class="px-3 py-2 text-xs font-medium text-center border-b text-n-blue-11 last:border-b-0 border-n-weak"
          >
            {{ interpolate(button.title) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Button template: a text bubble with stacked buttons underneath -->
    <div
      v-else-if="isButton"
      class="flex flex-col overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
    >
      <p class="p-3 text-sm break-words whitespace-pre-wrap text-n-slate-12">
        {{ text }}
      </p>
      <div class="flex flex-col border-t border-n-weak">
        <span
          v-for="(button, index) in buttons"
          :key="index"
          class="px-3 py-2 text-xs font-medium text-center border-b text-n-blue-11 last:border-b-0 border-n-weak"
        >
          {{ interpolate(button.title) }}
        </span>
      </div>
    </div>

    <!-- Quick replies: a text bubble with tappable chips below it -->
    <div v-else-if="isQuickReply" class="flex flex-col gap-2">
      <p
        class="p-3 text-sm break-words whitespace-pre-wrap border rounded-xl border-n-weak bg-n-solid-1 text-n-slate-12"
      >
        {{ text }}
      </p>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="(reply, index) in quickReplies"
          :key="index"
          class="px-3 py-1 text-xs font-medium border rounded-full border-n-blue-8 text-n-blue-11"
        >
          {{ quickReplyLabel(reply) }}
        </span>
      </div>
    </div>
  </div>
</template>
